import { ApolloClient, ApolloLink, CombinedGraphQLErrors, from, InMemoryCache, Observable } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import isExtractableFileDefault from 'apollo-upload-client/isExtractableFile.mjs';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { v4 as uuid } from 'uuid';

import { AppEndpointConfig } from '~/contexts/app-config';
import { getAuthToken, isLoggingOut } from '~/contexts/auth';
import * as ErrorDiagnostics from '~/error/app-error-diagnostics';
import { isReactNativeFile, ReactNativeFile } from '~/utils/file-format';
import { getNetworkReachability } from '~/contexts/network-service-state';
import { isTransientNetworkError } from '~/utils/network-error';

import { cacheConfig } from './apollo-cache';
import { customFetch } from './app-fetch';
import { LegacyWebSocketLink } from './legacy-ws-link';

const isExtractableFile = (value: unknown): value is File | Blob | ReactNativeFile => {
    return isExtractableFileDefault(value) || isReactNativeFile(value);
};

let refreshing = false;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pendingRequests: [any, any][] = [];

const resolvePendingRequests = (error?: Error) => {
    pendingRequests.forEach(([resolve, reject]) => (error ? reject(error) : resolve()));
    pendingRequests = [];
};

export const createAppClient = (
    { http, ws, host }: AppEndpointConfig,
    refreshAuthenticationToken: () => Promise<boolean>
) => {
    const uri = `${http}://${host}/member-api/graphql/`;

    const httpLink = new UploadHttpLink({
        uri,
        credentials: 'same-origin',
        headers: { 'Accept-Encoding': 'gzip' },
        fetch: customFetch,
        isExtractableFile
    });

    // Note! Websocket client always sends the current auth token on WS `connection_init`. The backend will verify
    // auth token and if not successful, immediately close the connection with close code 4004. The client will
    // receive the code and reconnect with new token.

    // Typically, any normal GraphQL query or mutation operation (over HTTPS) will renew the auth token using the
    // renewal mechanism implemented in `errorLink` below. In typical situations queries and mutations will happen
    // alongside subscription messages, so trying to re-connect with expired token is not a common scenario. However,
    // this can happen if the client closes the socket due to inactivity and never executes any other operations
    // before trying to re-open the connection. This would lead to a failure.

    // To experiment with this edge case, 1) reduce subscription client `inactivityTimeout` to e.g. 3 seconds,
    // 2) open a view with a subscription, e.g. chat, 3) close the view and wait > `inactivityTimeout` -> the connection
    // will close, 4) observe connection close with code 1000, 5) wait > JWT expiration + leeway 5) navigate back to
    // the view, 5) subscription tries to open the web socket with expired JWT token which will cause the socket to
    // close (with 4004 code)

    // In order to catch this 4004 on the client side, we must use a custom fork of (unmaintained)
    // `subscriptions-transport-ws` that will correctly report the close code to the subscription client `onDisconnect`
    // event handler. See https://github.com/Traktormaster/subscriptions-transport-ws/commit/63a689e9487c228d01ae60d1cc78204072a2fba0

    const client = new SubscriptionClient(`${ws}://${host}/member-api/ws/graphql`, {
        reconnect: true,
        lazy: true,
        connectionParams: () => ({
            authToken: getAuthToken()
        }),
        inactivityTimeout: 30 * 1000
    });

    client.onDisconnected(code => {
        if (code === 4004) {
            if (!refreshing) {
                refreshing = true;
                ErrorDiagnostics.log('Token expired in subscription client, refreshing.');
                refreshAuthenticationToken()
                    .then(() => {
                        resolvePendingRequests();
                    })
                    .catch(err => {
                        ErrorDiagnostics.error(err);
                        resolvePendingRequests(err);
                    })
                    .finally(() => {
                        refreshing = false;
                    });
            }
        }
        ErrorDiagnostics.log(`Subscription disconnected (${code})`, { notify: true });
    });

    ['error', 'connecting', 'reconnecting', 'connected', 'reconnected'].forEach(event =>
        client.on(event, async () => ErrorDiagnostics.log(`Subscription ${event}`, { notify: true }))
    );

    const wsLink: ApolloLink = new LegacyWebSocketLink(client);

    const authLink = setContext(async () => {
        const requestId = uuid();
        ErrorDiagnostics.setRequestId(requestId);

        let token = getAuthToken();

        if (!token && refreshing) {
            try {
                await new Promise<void>((resolve, reject) => {
                    pendingRequests.push([resolve, reject]);
                });
                token = getAuthToken();
            } catch {
                // Refresh failed, token still unavailable — request will fail downstream
            }
        }

        if (!token && !isLoggingOut()) {
            ErrorDiagnostics.error(new Error('Auth token not available when making authenticated request'));
        }

        return {
            headers: {
                'X-Request-ID': requestId,
                ...(token ? { Authorization: `JWT ${token}` } : {})
            }
        };
    });

    const retryLink = new RetryLink({
        delay: {
            initial: 500,
            max: 10000,
            jitter: true
        },
        attempts: {
            max: 5,
            retryIf: error => {
                if (getNetworkReachability() === 'service-unavailable') return false;
                return isTransientNetworkError(error);
            }
        }
    });

    const errorLink = onError(({ error, operation, forward }) => {
        // Inspired by https://able.bio/AnasT/apollo-graphql-async-access-token-refresh--470t1c8
        const tokenExpiredError =
            CombinedGraphQLErrors.is(error) &&
            error.errors.find(({ message }) =>
                ['You do not have permission to perform this action', 'Signature has expired'].includes(message)
            );
        if (error.message.includes('403') || tokenExpiredError) {
            if (refreshing) {
                return new Observable(observer => {
                    new Promise<void>((resolve, reject) => {
                        pendingRequests.push([resolve, reject]);
                    })
                        .then(() => {
                            forward(operation).subscribe(observer);
                        })
                        .catch(err => {
                            observer.error(err);
                        });
                });
            } else {
                refreshing = true;
                ErrorDiagnostics.log('Token expired in apollo error link, refreshing.');
                return new Observable(observer => {
                    refreshAuthenticationToken()
                        .then(() => {
                            resolvePendingRequests();
                            forward(operation).subscribe(observer);
                        })
                        .catch(err => {
                            ErrorDiagnostics.error(err);
                            resolvePendingRequests(err);
                            observer.error(err);
                        })
                        .finally(() => {
                            refreshing = false;
                        });
                });
            }
        }

        if (CombinedGraphQLErrors.is(error)) {
            error.errors.map(graphQLError => ErrorDiagnostics.log(`GraphQL error: ${graphQLError.message}`));
        }
    });

    const apolloClient = new ApolloClient({
        devtools: { enabled: __DEV__ },
        link: ApolloLink.from([
            ApolloLink.split(
                ({ query }) => {
                    const definition = getMainDefinition(query);
                    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
                },
                wsLink,
                from([errorLink, retryLink, authLink, httpLink])
            )
        ]),
        cache: new InMemoryCache(cacheConfig)
    });

    return apolloClient;
};
